'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { signOut } from 'next-auth/react';
import styles from './UserDropdown.module.css';

interface UserDropdownProps {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    role?: string;
  };
}

export default function UserDropdown({ user }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isAdminOrModerator = user.role === 'ADMIN' || user.role === 'MODERATOR';
  const displayName = user.name || user.email || 'Usuario';
  const initials = displayName.slice(0, 2).toUpperCase();

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        close();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  const handleLogout = () => {
    signOut({ callbackUrl: '/' });
  };

  return (
    <div className={styles.dropdown} ref={dropdownRef} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`Menú de usuario: ${displayName}`}
      >
        {user.image ? (
          <img src={user.image} alt="" className={styles.avatarImg} aria-hidden="true" />
        ) : (
          <span className={styles.avatar} aria-hidden="true">{initials}</span>
        )}
        <span className={styles.name}>{displayName}</span>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.menu} role="menu" aria-label="Opciones de usuario">
          <div className={styles.menuHeader}>
            {user.image ? (
              <img src={user.image} alt="" className={styles.menuAvatarImg} aria-hidden="true" />
            ) : (
              <span className={styles.menuAvatar} aria-hidden="true">{initials}</span>
            )}
            <div className={styles.menuInfo}>
              <span className={styles.menuName}>{displayName}</span>
              <span className={styles.menuRole}>{user.role || 'USER'}</span>
            </div>
          </div>

          <div className={styles.menuDivider} role="separator" />

          <Link
            href="/me"
            className={styles.menuItem}
            role="menuitem"
            onClick={close}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            Mi Sala
          </Link>

          {isAdminOrModerator && (
            <Link
              href="/admin"
              className={styles.menuItem}
              role="menuitem"
              onClick={close}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              Admin
            </Link>
          )}

          <div className={styles.menuDivider} role="separator" />

          <button
            className={styles.menuItem}
            role="menuitem"
            onClick={handleLogout}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar Sesión
          </button>
        </div>
      )}
    </div>
  );
}
